'use client'

import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import type React from 'react'
import { useRef } from 'react'
import { Tag } from '@/components/elementary/Tag'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
	SITE_HEADER_OFFSET_MOBILE,
	STICKY_HEADER_BOTTOM_THRESHOLD,
} from '@/constants/pricing.constants'
import { useRefSticky } from '@/hooks/useRefSticky'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'
import type { PricingItem } from '@/types/api/pricing.types'

export function PlanTabs({
	data,
	currentTab,
	setCurrentTab,
	renderContent,
	className,
}: {
	data?: Data.Component<'shared.pricing-plan-detail'>[] | null
	currentTab: string
	setCurrentTab: (value: string) => void
	renderContent?: (item: PricingItem) => React.ReactNode
	className?: string
}) {
	const t = useTranslations('pricing')

	const stickyRef = useRef<HTMLDivElement>(null)
	const { isSticky } = useRefSticky(
		stickyRef,
		SITE_HEADER_OFFSET_MOBILE,
		STICKY_HEADER_BOTTOM_THRESHOLD,
	)
	return (
		<Tabs
			data-test-id={PRICING_TEST_IDS.CONTENT_TAB}
			value={currentTab}
			onValueChange={setCurrentTab}
			className={className}
		>
			<TabsList
				className={cn(
					'sticky flex justify-center w-auto px-0 -mx-6  z-20 top-[var(--site-header-offset-mobile)] pb-0 h-auto rounded-none bg-brand-black border-b',
					isSticky
						? 'pt-3 border-neutral-light-100 transition-colors duration-150 ease-in-out'
						: 'border-transparent',
				)}
				ref={stickyRef}
			>
				<div className="flex overflow-auto px-6 pb-3 w-auto no-scrollbar">
					{data?.map((item) => (
						<TabsTrigger
							value={String(item?.title)}
							key={`${item?.id}-tab`}
							data-test-id={PRICING_TEST_IDS.CONTENT_TAB_TRIGGER}
							className="group/tabs-trigger px-5 py-3 text-neutral-light-100 data-[state=active]:bg-neutral-light-100 data-[state=active]:text-brand-black transition-colors duration-200 ease-in-out"
						>
							<span className="lok-text-body-bold-medium">{item?.title}</span>
							{item?.mostPopular && (
								<Tag
									name={t('mostPopular')}
									size="small"
									className="w-fit text-sm lg:text-sm ml-1.25 -mr-1.25 group-data-[state=active]/tabs-trigger:text-neutral-light-100 group-data-[state=active]/tabs-trigger:bg-brand-black transition-colors duration-200 ease-in-out"
									fontWeight="font-bold"
									active
									customColors={{
										backgroundColor: 'neutral-light-100',
										borderColor: 'neutral-light-100',
										textColor: 'brand-black',
									}}
								/>
							)}
						</TabsTrigger>
					))}
				</div>
			</TabsList>

			{data?.map((item) => (
				<TabsContent
					className="w-full"
					value={String(item?.title)}
					key={`${item?.id}-content`}
					data-test-id={PRICING_TEST_IDS.CONTENT_TAB_CONTENT}
				>
					{renderContent ? renderContent(item) : null}
				</TabsContent>
			))}
		</Tabs>
	)
}
